import Head from 'next/head';
import JsonLd from './JsonLd';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildBreadcrumbListSchema
} from '@/utils/json-ld-schemas';
import { getImagePath } from '@/utils/images';

const SITE_NAME = 'Leadhub';
const DEFAULT_OG_IMAGE = process.env.NEXT_PUBLIC_OG_IMAGE || `${process.env.NEXT_PUBLIC_SITE_URL}/og-image.png`;
const DEFAULT_OG_IMAGE_WIDTH = 1200;
const DEFAULT_OG_IMAGE_HEIGHT = 630;

// Map internal locale codes to Open Graph locale identifiers
const OG_LOCALE_MAP = {
  cs: 'cs_CZ',
  en: 'en_US',
  pl: 'pl_PL'
};

const PageHead = ({
  title,
  description,
  path,
  locale,
  localizations,
  isPreview,
  noIndex = false,
  ogImage = null,
  breadcrumbs = null,
  extraSchemas = []
}) => {
  // Per-page OG image from Strapi falls back to the global default if missing
  const ogImageUrl = ogImage?.url ? getImagePath(ogImage) : DEFAULT_OG_IMAGE;
  const ogImageWidth = ogImage?.width || DEFAULT_OG_IMAGE_WIDTH;
  const ogImageHeight = ogImage?.height || DEFAULT_OG_IMAGE_HEIGHT;
  // For Czech pages, always use clean path without /cs prefix for canonical
  let canonicalPath = path || '/';
  if (locale === 'cs') {
    canonicalPath = path || '/';
  } else {
    canonicalPath = `/${locale}${path || '/'}`;
  }

  const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL}${canonicalPath}`;
  const ogLocale = OG_LOCALE_MAP[locale] || OG_LOCALE_MAP.cs;

  // JSON-LD schemas: Organization + WebSite are global, WebPage + BreadcrumbList are per-page.
  // Page.js can pass extraSchemas (e.g. FAQPage from QuestionsBlock or Article for glossary).
  const isHomepage = !path || path === '/';
  const jsonLdSchemas = [
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildWebPageSchema({ title, description, canonicalUrl, locale }),
    !isHomepage &&
      buildBreadcrumbListSchema({
        path,
        locale,
        title,
        extraCrumbs: breadcrumbs
      }),
    ...extraSchemas
  ];

  // Build alternate language URLs
  const alternateUrls = [
    {
      hreflang: locale || 'cs',
      href: canonicalUrl
    }
  ];

  // Add localizations if available
  (localizations || []).forEach((localization) => {
    const localizedPath = localization.path;
    const localizedLocale = localization.locale;

    let localizedUrl;
    if (localizedLocale === 'cs') {
      localizedUrl = `${process.env.NEXT_PUBLIC_SITE_URL}${localizedPath}`;
    } else {
      localizedUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/${localizedLocale}${localizedPath}`;
    }

    alternateUrls.push({
      hreflang: localizedLocale,
      href: localizedUrl
    });
  });

  return (
    <>
      <JsonLd data={jsonLdSchemas} />
      <Head>
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Prevent indexing of preview pages or pages explicitly marked noIndex in Strapi */}
      {(isPreview || noIndex) && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Language alternates */}
      {alternateUrls.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {Object.entries(OG_LOCALE_MAP)
        .filter(([code]) => code !== (locale || 'cs'))
        .map(([, ogCode]) => (
          <meta key={ogCode} property="og:locale:alternate" content={ogCode} />
        ))}
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content={ogImageWidth} />
      <meta property="og:image:height" content={ogImageHeight} />
      <meta property="og:image:alt" content={title} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={title} />
      </Head>
    </>
  );
};
export default PageHead;
